import { Injectable, OnDestroy, Inject, PLATFORM_ID, signal, computed } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { AuthService as CoreAuthService } from './auth.service';
import { AuthService } from '../../services/auth/auth.service';
import { SessionContextService } from './session-context.service';
import { isTokenExpired, getTokenTimeToExpiry } from './jwt-helpers';

export interface SessionExpiryWarning {
  timeRemaining: number; // segundos restantes
  showWarning: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthTokenMonitorService implements OnDestroy {
  private timerId: any = null;
  private warningTimerId: any = null;
  private readonly checkIntervalMs = 30_000; // 30s - más frecuente para mejor UX
  private readonly warningThresholdSeconds = 120; // 2 minutos antes de expirar
  private readonly countdownIntervalMs = 1_000; // 1s para countdown

  // Signals para estado reactivo
  private _timeRemaining = signal<number>(0);
  private _showWarning = signal<boolean>(false);
  private _showDelegatedWarning = signal<boolean>(false);
  private _isRefreshing = signal<boolean>(false);

  // Computed signals públicos
  readonly timeRemaining = computed(() => this._timeRemaining());
  readonly showWarning = computed(() => this._showWarning());
  readonly showDelegatedWarning = computed(() => this._showDelegatedWarning());
  readonly isRefreshing = computed(() => this._isRefreshing());

  // Subject para eventos de expiración
  readonly sessionExpired$ = new Subject<void>();
  readonly sessionExtended$ = new Subject<void>();

  constructor(
    private readonly coreAuth: CoreAuthService,
    private readonly authService: AuthService,
    private readonly sessionContext: SessionContextService,
    private readonly router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.start();
    try {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    } catch {}
  }

  start(): void {
    if (this.timerId) return;
    this.checkNow();
    this.timerId = setInterval(() => this.checkNow(), this.checkIntervalMs);
  }

  stop(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    this.stopWarningCountdown();
  }

  ngOnDestroy(): void {
    this.stop();
    try {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    } catch {}
  }

  private onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.checkNow();
    }
  };

  private checkNow(): void {
    const user = this.coreAuth.getCurrentUser();
    if (!user || !user.accessToken) {
      this._showWarning.set(false);
      this._showDelegatedWarning.set(false);
      this._timeRemaining.set(0);
      return;
    }

    // Verificar si ya expiró
    if (isTokenExpired(user.accessToken)) {
      this.handleSessionExpired();
      return;
    }

    // Obtener tiempo restante
    const timeToExpiry = getTokenTimeToExpiry(user.accessToken);
    this._timeRemaining.set(timeToExpiry);

    // Sesiones delegadas: mostrar diálogo informativo con countdown
    if (this.sessionContext.isDelegatedSession()) {
      if (timeToExpiry <= this.warningThresholdSeconds && timeToExpiry > 0) {
        if (!this._showDelegatedWarning()) {
          this._showDelegatedWarning.set(true);
          this.startWarningCountdown();
        }
      } else {
        this._showDelegatedWarning.set(false);
      }
      return;
    }

    // Sesiones normales: mostrar warning si quedan ≤120s
    if (timeToExpiry <= this.warningThresholdSeconds && timeToExpiry > 0) {
      if (!this._showWarning()) {
        this._showWarning.set(true);
        this.startWarningCountdown();
      }
    } else {
      this._showWarning.set(false);
      this.stopWarningCountdown();
    }
  }

  private startWarningCountdown(): void {
    if (this.warningTimerId) return;

    this.warningTimerId = setInterval(() => {
      const remaining = this._timeRemaining();
      if (remaining <= 0) {
        this.handleSessionExpired();
        return;
      }
      this._timeRemaining.set(remaining - 1);
    }, this.countdownIntervalMs);
  }

  private stopWarningCountdown(): void {
    if (this.warningTimerId) {
      clearInterval(this.warningTimerId);
      this.warningTimerId = null;
    }
  }

  /**
   * Maneja la expiración de sesión
   * Cierra modales, limpia sesión y redirige a login
   */
  private handleSessionExpired(): void {
    this.stopWarningCountdown();
    this._showWarning.set(false);
    this._showDelegatedWarning.set(false);
    this._timeRemaining.set(0);

    // Emitir evento de expiración
    this.sessionExpired$.next();

    // Sesiones delegadas → redirect al home con parámetro
    if (this.sessionContext.isDelegatedSession()) {
      try {
        this.router.navigate(['/'], {
          queryParams: { supportSession: 'expired' },
        });
      } catch {}
      return;
    }

    // Sesiones normales → logout + redirect a login
    this.coreAuth.logout();
    this.authService.clearSession();

    // Redirigir a login con parámetro de sesión expirada
    try {
      this.router.navigate(['/login'], {
        queryParams: { sessionExpired: '1' },
      });
    } catch {}
  }

  /**
   * Intenta extender la sesión renovando el token
   * @returns Promise<boolean> - true si se renovó exitosamente
   */
  async extendSession(): Promise<boolean> {
    if (this._isRefreshing()) {
      return false;
    }

    // Sesiones delegadas no pueden extenderse
    if (this.sessionContext.isDelegatedSession()) {
      return false;
    }

    this._isRefreshing.set(true);

    try {
      await this.authService.refreshTokens();

      // Recargar usuario con nuevos tokens
      const accessToken = this.authService.getAccessToken();
      const idToken = this.authService.getIdToken();
      const refreshToken = this.authService.getRefreshToken();

      if (accessToken && idToken && refreshToken) {
        const currentUser = this.coreAuth.getCurrentUser();
        if (currentUser) {
          this.coreAuth.setCurrentUser({
            ...currentUser,
            accessToken,
            idToken,
            refreshToken,
          });
        }
      }

      // Resetear estado de advertencia
      this._showWarning.set(false);
      this.stopWarningCountdown();

      // Actualizar tiempo restante
      if (accessToken) {
        this._timeRemaining.set(getTokenTimeToExpiry(accessToken));
      }

      // Emitir evento de sesión extendida
      this.sessionExtended$.next();

      console.log('Sesión extendida exitosamente');
      return true;
    } catch (error) {
      console.error('❌ Error al extender sesión:', error);

      // Si el token sigue siendo válido, no cerrar sesión por un fallo transitorio
      const user = this.coreAuth.getCurrentUser();
      if (user?.accessToken && !isTokenExpired(user.accessToken)) {
        console.warn('⚠️ Refresh falló, pero el token aún es válido. Continuando.');
        return false;
      }

      // Si realmente expiró, manejar como expiración
      this.handleSessionExpired();
      return false;
    } finally {
      this._isRefreshing.set(false);
    }
  }

  /**
   * Cierra sesión manualmente (usuario eligió no extender)
   */
  declineExtension(): void {
    this.handleSessionExpired();
  }

  /**
   * Formatea el tiempo restante como MM:SS
   */
  formatTimeRemaining(): string {
    const seconds = this._timeRemaining();
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Helper público: ¿el token ya expiró realmente?
   */
  isTokenCurrentlyExpired(): boolean {
    const user = this.coreAuth.getCurrentUser();
    if (!user || !user.accessToken) return true;
    return isTokenExpired(user.accessToken);
  }

  /**
   * Dev helper: forzar el diálogo de advertencia (dev only)
   */
  forceShowExpiryWarning(seconds?: number): void {
    if (seconds) {
      this._timeRemaining.set(seconds);
    }
    if (this.sessionContext.isDelegatedSession()) {
      this._showDelegatedWarning.set(true);
    } else {
      this._showWarning.set(true);
    }
    this.startWarningCountdown();
  }
}
